import React, { useState, useEffect } from "react";
import { getUsers, createUser, deleteUser } from "../api";

/**
 * Panneau d'administration — gestion des utilisateurs (admin uniquement).
 * Créer un utilisateur avec nom, emoji et couleur.
 * Supprimer un utilisateur (sauf l'admin).
 */

// Emojis prédéfinis pour faciliter le choix
const EMOJI_OPTIONS = [
  "\uD83D\uDC77",  // 👷
  "\uD83D\uDC68",  // 👨
  "\uD83D\uDC69",  // 👩
  "\uD83D\uDC66",  // 👦
  "\uD83D\uDC67",  // 👧
  "\uD83E\uDDD1",  // 🧑
  "\uD83D\uDE4B",  // 🙋
  "\uD83D\uDC68\u200D\uD83D\uDD27",  // 👨‍🔧
];

const COLOR_OPTIONS = [
  "#81C784", "#64B5F6", "#E57373", "#FFB74D",
  "#BA68C8", "#4DB6AC", "#FF8A65", "#A1887F",
];

function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [username, setUsername] = useState("");
  const [emoji, setEmoji] = useState(EMOJI_OPTIONS[0]);
  const [color, setColor] = useState(COLOR_OPTIONS[1]);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);

  const fetchUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (err) {
      console.error("Erreur chargement utilisateurs:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!username.trim()) {
      setError("Le nom est requis");
      return;
    }

    setCreating(true);
    setError("");

    try {
      await createUser({ username: username.trim(), emoji, color });
      setUsername("");
      setShowForm(false);
      fetchUsers();
    } catch (err) {
      setError(err.message || "Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Supprimer l'utilisateur "${user.username}" et tous ses articles ?`)) return;
    try {
      await deleteUser(user.id);
      fetchUsers();
    } catch (err) {
      window.alert(err.message || "Erreur lors de la suppression");
    }
  };

  if (loading) return <p className="loading-text">Chargement...</p>;

  return (
    <div className="admin-panel">
      <h2 className="admin-title">Gestion des utilisateurs</h2>

      {/* Liste des utilisateurs */}
      <div className="admin-users">
        {users.map((user) => (
          <div key={user.id} className="admin-user-card">
            <span className="admin-user-emoji" style={{ backgroundColor: `${user.color}25`, color: user.color }}>
              {user.emoji}
            </span>
            <span className="admin-user-name">{user.username}</span>
            {user.is_admin && <span className="admin-badge">Admin</span>}
            {!user.is_admin && (
              <button className="admin-user-delete" onClick={() => handleDelete(user)} title="Supprimer">
                &#x1F5D1;
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Bouton / formulaire de création */}
      {!showForm ? (
        <button className="btn-primary admin-add-btn" onClick={() => setShowForm(true)}>
          + Ajouter un utilisateur
        </button>
      ) : (
        <form onSubmit={handleCreate} className="admin-form">
          <div className="form-group">
            <label htmlFor="new-username">Nom d'utilisateur</label>
            <input
              id="new-username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Prénom ou pseudo"
              maxLength={50}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label>Emoji</label>
            <div className="admin-emoji-grid">
              {EMOJI_OPTIONS.map((e) => (
                <button
                  key={e}
                  type="button"
                  className={`admin-emoji-btn ${emoji === e ? "selected" : ""}`}
                  onClick={() => setEmoji(e)}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Couleur</label>
            <div className="admin-color-grid">
              {COLOR_OPTIONS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`admin-color-btn ${color === c ? "selected" : ""}`}
                  style={{ backgroundColor: c }}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </div>

          {error && <p className="login-error">{error}</p>}

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => { setShowForm(false); setError(""); }}>
              Annuler
            </button>
            <button type="submit" className="btn-primary" disabled={creating || !username.trim()}>
              {creating ? "Création..." : "Créer"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default AdminPanel;
